(function () {
  'use strict';

  // BSPO Search V20 - V19 design and thumbnails unchanged; segmented on-demand search index.

  var config = window.BSPO_SEARCH_CONFIG || {};
  var indexPromise = null;
  var activeAutocomplete = null;
  var autocompleteTimer = null;
  var requestNumber = 0;
  var productJsonCache = {};
  var productPageInfoCache = {};

  var PRIORITY = {
    o: 1000, // other.part
    r: 900,  // custom.replaces_part
    h: 875,  // custom.hydroquip_part - supplier name never displayed
    d: 800,  // product.model
    w: 300   // works.with
  };

  function normalizeExact(value) {
    return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function normalizeText(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // V20 routes each search to just the index shards that can contain a hit.
  // Assets live beside the existing bspo-search-index.js Shopify asset.
  // Keep the old index asset untouched as a rollback option.
  var shardCache = {};
  var manifestPromise = null;
  var DEFAULT_SHARDS = 16;

  function indexAssetUrl(filename) {
    if (!config.indexUrl) return '';
    // Replace basename, not the whole Shopify CDN URL; this works with
    // relative paths and versioned Shopify CDN asset URLs.
    var url = String(config.indexUrl);
    var pos = url.indexOf('?');
    var base = pos === -1 ? url : url.substring(0, pos);
    var slash = base.lastIndexOf('/');
    if (slash === -1) return '';
    return base.substring(0, slash + 1) + filename;
  }

  function loadScript(src, validate) {
    return new Promise(function (resolve, reject) {
      if (!src) { reject(new Error('No BSPO search asset URL.')); return; }
      var el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.onload = function () {
        if (validate()) resolve();
        else reject(new Error('BSPO search asset missing expected data: ' + src));
      };
      el.onerror = function () { reject(new Error('BSPO search asset unavailable: ' + src)); };
      document.head.appendChild(el);
    });
  }

  function loadManifest() {
    if (window.BSPO_SEARCH_MANIFEST) return Promise.resolve(window.BSPO_SEARCH_MANIFEST);
    if (!manifestPromise) {
      manifestPromise = loadScript(indexAssetUrl('bspo-search-index-manifest.js'),
        function () { return !!window.BSPO_SEARCH_MANIFEST; })
        .then(function () { return window.BSPO_SEARCH_MANIFEST; });
    }
    return manifestPromise;
  }

  function shardIds(manifest, query) {
    var n = manifest.n || DEFAULT_SHARDS;
    var tokens = normalizeText(query).split(' ').filter(Boolean);
    var all = [];
    for (var i = 0; i < n; i++) all.push(i);
    if (!tokens.length) return all;

    // A shard is eligible when it could contain ALL query tokens in at least
    // one searchable field. Using a per-token union, then a cross-token
    // intersection, would omit matches when tokens occur in different fields.
    // Therefore use the union of all token candidates, which is conservative.
    var allMask = (1 << n) - 1;
    var mask = 0;
    var searchableTokens = tokens.slice();
    var exact = normalizeExact(query);
    if (exact.length >= 2) searchableTokens.push(exact);
    searchableTokens.forEach(function (t) {
      if (t.length < 2) { mask = allMask; return; }
      var gram = t.substring(0, Math.min(3, t.length));
      // With 3-char routing, two-letter terms cannot use arbitrary 2-gram keys.
      // Conservatively load everything for two-letter queries.
      if (gram.length < 3) { mask = allMask; return; }
      mask |= (manifest.grams && manifest.grams[gram]) || 0;
    });
    // Unknown routing keys can be real recently-added products, which the
    // manifest cannot know. Native Shopify results remain merged in any case.
    var chosen = all.filter(function (i) { return !!(mask & (1 << i)); });
    return chosen;
  }

  function loadShard(id) {
    if (shardCache[id]) return shardCache[id];
    shardCache[id] = loadScript(
      indexAssetUrl('bspo-search-index-part-' + ('0' + id).slice(-2) + '.js'),
      function () { return !!(window.BSPO_SEARCH_SHARDS && window.BSPO_SEARCH_SHARDS[id]); }
    ).then(function () { return window.BSPO_SEARCH_SHARDS[id]; });
    return shardCache[id];
  }

  function assembleShards(shards) {
    var merged = {p: [], e: {}, x: [], g: []};
    shards.forEach(function (shard) {
      var offset = merged.p.length;
      (shard.p || []).forEach(function (row) { merged.p.push(row); });
      Object.keys(shard.e || {}).forEach(function (key) {
        if (!merged.e[key]) merged.e[key] = [];
        shard.e[key].forEach(function (pair) {
          merged.e[key].push([pair[0] + offset, pair[1]]);
        });
      });
      (shard.x || []).forEach(function (row) {
        merged.x.push([row[0] + offset, row[1], row[2]]);
      });
      (shard.g || []).forEach(function (key) { merged.g.push(key); });
    });
    merged.g.sort();
    return merged;
  }

  function loadIndex(query) {
    return loadManifest().then(function (manifest) {
      var ids = shardIds(manifest, query || '');
      return Promise.all(ids.map(loadShard)).then(assembleShards);
    });
  }

  function isSearchForm(form) {
    return !!(form && form.matches('.bspo-search-form, .bspo-home-search-form, form.search-bar'));
  }

  function getQueryInput(form) {
    return form ? form.querySelector('input[name="q"]') : null;
  }

  function isNativeMpn(index, query) {
    var key = normalizeExact(query);
    if (!key || !index.g) return false;

    var low = 0, high = index.g.length - 1;
    while (low <= high) {
      var mid = (low + high) >> 1;
      if (index.g[mid] === key) return true;
      if (index.g[mid] < key) low = mid + 1;
      else high = mid - 1;
    }
    return false;
  }

  function reason(kind, query) {
    if (kind === 'o') return 'Alternate Part Number Match';
    if (kind === 'r') return 'Replacement Match for “' + query + '”';
    if (kind === 'h') return 'Exact Part Number Match';
    if (kind === 'd') return 'Model Match';
    if (kind === 'w') return 'Compatibility Match';
    return 'Product Match';
  }

  function dedupe(matches) {
    var best = {};
    matches.forEach(function (m) {
      var current = best[m.i];
      if (!current || m.score > current.score) best[m.i] = m;
    });

    return Object.keys(best).map(function (k) {
      return best[k];
    }).sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.i - b.i;
    });
  }

  function findMatches(index, query) {
    var exactQuery = normalizeExact(query);
    var textQuery = normalizeText(query);
    var matches = [];

    if (exactQuery && index.e && index.e[exactQuery]) {
      index.e[exactQuery].forEach(function (pair) {
        matches.push({
          i: pair[0],
          kind: pair[1],
          score: PRIORITY[pair[1]] || 500,
          exact: true
        });
      });
    }

    if (textQuery && textQuery.length >= 2 && index.x) {
      var tokens = textQuery.split(' ').filter(Boolean);

      index.x.forEach(function (row) {
        var pidx = row[0];
        var model = row[1] || '';
        var works = row[2] || '';

        if (model) {
          var modelAll = tokens.every(function (t) {
            return model.indexOf(t) !== -1;
          });

          if (modelAll) {
            matches.push({
              i: pidx,
              kind: 'd',
              score: model === textQuery ? 800 : 525,
              exact: model === textQuery
            });
          }
        }

        if (works) {
          var worksAll = tokens.every(function (t) {
            return works.indexOf(t) !== -1;
          });

          if (worksAll) {
            matches.push({
              i: pidx,
              kind: 'w',
              score: 300,
              exact: false
            });
          }
        }
      });
    }

    return dedupe(matches);
  }

  // Shopify live-search is an autocomplete feed and is capped upstream.
  // Search the full BSPO index by product title so generic terms such as
  // "union" are not limited to the first live-search suggestions.
  function findTitleMatches(index, query) {
    var tokens = normalizeText(query).split(' ').filter(Boolean);
    if (!tokens.length || !index || !Array.isArray(index.p)) return [];
    var results = [];
    index.p.forEach(function (row, i) {
      if (!row || !row[0] || !row[1]) return;
      var title = normalizeText(row[1]);
      if (!tokens.every(function (token) { return title.indexOf(token) !== -1; })) return;
      results.push({
        i: i,
        kind: 't',
        score: title === normalizeText(query) ? 1500 : (title.indexOf(normalizeText(query)) !== -1 ? 1200 : 1100),
        exact: title === normalizeText(query)
      });
    });
    return results;
  }

  function getExactKeys(index) {
    return Object.keys(index.e || {});
  }

  function findAutocompleteMatches(index, query) {
    var matches = findMatches(index, query);
    var exactQuery = normalizeExact(query);

    if (exactQuery.length >= 3) {
      var keys = getExactKeys(index);
      var prefixHits = 0;

      for (var k = 0; k < keys.length && prefixHits < 80; k++) {
        var key = keys[k];
        if (key.indexOf(exactQuery) !== 0 || key === exactQuery) continue;

        var pairs = index.e[key] || [];
        pairs.forEach(function (pair) {
          if (pair[1] === 'o' || pair[1] === 'r' || pair[1] === 'h') {
            matches.push({
              i: pair[0],
              kind: pair[1],
              score: (PRIORITY[pair[1]] || 500) - 150,
              exact: false,
              prefix: true
            });
          }
        });
        prefixHits++;
      }
    }

    return dedupe(matches).slice(0, 12);
  }

  function product(index, match) {
    var p = index.p[match.i];
    return {
      handle: p[0],
      title: p[1],
      kind: match.kind,
      score: match.score,
      exact: match.exact
    };
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function moneyFromCents(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return '';
    return '$' + (n / 100).toFixed(2);
  }

  function isPartNumberLike(query) {
    var value = String(query || '').trim();
    return value.length >= 4 && !/\s/.test(value) && /[0-9]/.test(value);
  }

  function isDirectQueryMatch(item, query) {
    var key = normalizeExact(query);
    if (!key) return false;
    var titleKey = normalizeExact(item && item.title);
    var handleKey = normalizeExact(item && item.handle);
    return titleKey.indexOf(key) !== -1 || handleKey === key;
  }

  function statusText(info) {
    if (!info || !info.status) return '';
    if (info.status === 'replacement') {
      return 'VIEW REPLACEMENT OPTIONS' + (info.replacement ? ' — ' + info.replacement : '');
    }
    return 'DISCONTINUED — VIEW DETAILS';
  }

  function extractHandle(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]).toLowerCase() : '';
  }

  function fetchNativeLiveSearch(query) {
    var url = '/search?view=livesearch&type=product&q=' + encodeURIComponent(query);

    return fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Native live search failed.');
      return response.text();
    }).then(function (text) {
      var data = JSON.parse(text);
      if (!Array.isArray(data)) return [];

      return data.filter(function (item) {
        return item && item.title && item.url;
      }).map(function (item) {
        return {
          source: 'native',
          handle: extractHandle(item.url),
          title: item.title || '',
          url: item.url || '',
          thumb: item.thumb || '',
          price: item.price || '',
          price2: item.price2 || '',
          id: item.id || ''
        };
      });
    }).catch(function () {
      return [];
    });
  }

  function mergeAutocomplete(nativeResults, index, customMatches, query) {
    var merged = [];
    var seen = {};
    var nativeByHandle = {};

    nativeResults.forEach(function (item) {
      var nativeKey = String(item.handle || '').toLowerCase();
      if (nativeKey) nativeByHandle[nativeKey] = item;
    });

    customMatches.forEach(function (match) {
      var p = product(index, match);
      var key = String(p.handle || '').toLowerCase();
      if (!key || seen[key]) return;

      var nativeMatch = nativeByHandle[key] || null;
      var item = {
        source: 'custom',
        handle: p.handle,
        title: p.title,
        url: '/products/' + encodeURIComponent(p.handle)
          + '?bspo_q=' + encodeURIComponent(query)
          + '&bspo_match=' + encodeURIComponent(p.kind),
        thumb: nativeMatch ? upgradeImageUrl(nativeMatch.thumb) : '',
        price: nativeMatch ? nativeMatch.price : '',
        reason: autocompleteReason(match, query),
        score: p.score + 10000
      };

      // Exact-looking part-number hits are verified against the product page
      // before any price is trusted. This prevents stale native live-search
      // pricing from appearing on discontinued/replacement products.
      if (isPartNumberLike(query) && isDirectQueryMatch(item, query)) item.price = '';

      seen[key] = true;
      merged.push(item);
    });

    nativeResults.forEach(function (item) {
      var key = item.handle || item.url.toLowerCase();
      if (seen[key]) return;

      var mergedItem = {
        source: 'native',
        handle: item.handle,
        title: item.title,
        url: item.url,
        thumb: upgradeImageUrl(item.thumb),
        price: item.price,
        reason: 'Product Match',
        score: 100
      };

      if (isPartNumberLike(query) && isDirectQueryMatch(mergedItem, query)) mergedItem.price = '';

      seen[key] = true;
      merged.push(mergedItem);
    });

    return merged.slice(0, 10);
  }

  function addStyles() {
    if (document.getElementById('bspo-search-v13-styles')) return;

    var style = document.createElement('style');
    style.id = 'bspo-search-v13-styles';
    style.textContent =
      '.bspo-search-autocomplete{position:fixed;z-index:2147483647;background:#fff;border:2px solid #0A70B0;border-radius:8px;box-shadow:0 10px 28px rgba(10,112,176,.20);overflow:hidden;display:none;max-height:440px;overflow-y:auto}' +
      '.bspo-search-autocomplete.is-open{display:block}' +
      '.bspo-search-autocomplete-item{display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid #bfd7e5;background:#fff;text-decoration:none!important}' +
      '.bspo-search-autocomplete-item:last-of-type{border-bottom:0}' +
      '.bspo-search-autocomplete-item:hover,.bspo-search-autocomplete-item.is-active{background:#eef7fc}' +
      '.bspo-search-autocomplete-thumb{width:42px;height:42px;object-fit:contain;flex:0 0 42px;background:#fff;border:1px solid #d7e5ed;border-radius:5px}' +
      '.bspo-search-autocomplete-copy{min-width:0;flex:1 1 auto}' +
      '.bspo-search-autocomplete-title{display:block;color:#0A70B0;font-size:13px;line-height:1.3;font-weight:800}' +
      '.bspo-search-autocomplete-reason{display:block;margin-top:2px;color:#555;font-size:11px;line-height:1.25;font-weight:700}' +
      '.bspo-search-autocomplete-price{display:block;margin-top:2px;color:#333;font-size:11px;line-height:1.2;font-weight:800}' +
      '.bspo-search-autocomplete-status{display:block;margin-top:3px;color:#9b1c1c;font-size:11px;line-height:1.25;font-weight:900}' +
      '.bspo-search-autocomplete-footer{display:block;padding:10px 12px;background:#eef7fc;border-top:1px solid #bfd7e5;color:#0A70B0;font-size:12px;line-height:1.25;font-weight:900;text-decoration:none!important;text-align:center}' +
      '.bspo-search-autocomplete-empty{display:block;padding:16px 14px;background:#fff;color:#333;font-size:13px;line-height:1.4;font-weight:700;text-align:center}' +
      '.bspo-unified-search-wrap{max-width:1200px;margin:0 auto;padding:18px 15px 38px}' +
      '.bspo-unified-search-header{margin:0 0 16px;color:#0A70B0;font-size:22px;line-height:1.3;font-weight:800}' +
      '.bspo-unified-search-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px}' +
      '.bspo-unified-card{display:flex;flex-direction:column;min-width:0;background:#fff;border:2px solid #0A70B0;border-radius:10px;overflow:hidden;box-shadow:0 4px 14px rgba(10,112,176,.10);text-decoration:none!important;transition:all .18s ease}' +
      '.bspo-unified-card:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(10,112,176,.18)}' +
      '.bspo-unified-card-image-wrap{display:flex;align-items:center;justify-content:center;height:198px;padding:10px;background:#fff;box-sizing:border-box}' +
      '.bspo-unified-card-image{display:block;width:100%;height:100%;object-fit:contain}' +
      '.bspo-unified-card-body{padding:12px;border-top:1px solid #bfd7e5}' +
      '.bspo-unified-card-title{display:block;color:#0A70B0;font-size:13px;line-height:1.35;font-weight:800}' +
      '.bspo-unified-card-reason{display:block;margin-top:5px;color:#555;font-size:11px;line-height:1.25;font-weight:700}' +
      '.bspo-unified-card-price{display:block;margin-top:6px;color:#333;font-size:13px;line-height:1.2;font-weight:900}' +
      '.bspo-unified-card-status{display:block;margin-top:6px;color:#9b1c1c;font-size:12px;line-height:1.3;font-weight:900}' +
      '.bspo-unified-no-results{padding:18px;border:2px solid #0A70B0;border-radius:10px;background:#f5faff;color:#333;font-size:15px;line-height:1.5}' +
      '.bspo-search-show-more{display:block;margin:22px auto 8px;padding:13px 24px;border:2px solid #0A70B0;border-radius:8px;background:#0A70B0;color:#fff;font-weight:900;font-size:15px;cursor:pointer}' +
      '.bspo-search-show-more:hover,.bspo-search-show-more:focus{background:#fff;color:#0A70B0}' +
      '.bspo-search-count{margin:0 0 13px;color:#455866;font-size:13px;font-weight:700}' +
      '.bspo-search-catalog-link{margin:15px 0;text-align:center;font-size:13px;font-weight:800}' +
      '.bspo-pdp-search-match{margin:4px 0 10px;color:#0A70B0;font-size:12px;line-height:1.3;font-weight:800}' +
      'body.bspo-search-engaged .search-results,body.bspo-custom-search-open .search-results,' +
      'body.bspo-search-engaged #search-results,body.bspo-custom-search-open #search-results,' +
      'body.bspo-search-engaged .ajax-search-results,body.bspo-custom-search-open .ajax-search-results,' +
      'body.bspo-search-engaged .quickSearchResultsWrap,body.bspo-custom-search-open .quickSearchResultsWrap,' +
      'body.bspo-search-engaged .quickSearchResults,body.bspo-custom-search-open .quickSearchResults,' +
      'body.bspo-search-engaged .search-results-wrapper,body.bspo-custom-search-open .search-results-wrapper,' +
      'body.bspo-search-engaged .search-dropdown,body.bspo-custom-search-open .search-dropdown,' +
      'body.bspo-search-engaged .search-autocomplete,body.bspo-custom-search-open .search-autocomplete,' +
      'body.bspo-search-engaged .live-search,body.bspo-custom-search-open .live-search,' +
      'body.bspo-search-engaged .live-search-results,body.bspo-custom-search-open .live-search-results,' +
      'body.bspo-search-engaged [class*=\"quickSearch\"],body.bspo-custom-search-open [class*=\"quickSearch\"],' +
      'body.bspo-search-engaged [class*=\"liveSearch\"],body.bspo-custom-search-open [class*=\"liveSearch\"],' +
      'body.bspo-search-engaged [class*=\"livesearch\"],body.bspo-custom-search-open [class*=\"livesearch\"],' +
      '.bspo-native-search-hidden{display:none!important;visibility:hidden!important;opacity:0!important;pointer-events:none!important;max-height:0!important;overflow:hidden!important}' +
      '@media(max-width:991px){.bspo-unified-search-grid{grid-template-columns:repeat(3,minmax(0,1fr))}}' +
      '@media(max-width:767px){.bspo-unified-search-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.bspo-unified-card-image-wrap{height:165px}}' +
      '@media(max-width:480px){.bspo-unified-search-wrap{padding:12px 10px 28px}.bspo-unified-search-grid{grid-template-columns:1fr}.bspo-unified-card{display:grid;grid-template-columns:125px 1fr}.bspo-unified-card-image-wrap{height:125px}.bspo-unified-card-body{border-top:0;border-left:1px solid #bfd7e5}}';

    document.head.appendChild(style);
  }

  function ensureAutocomplete() {
    var box = document.getElementById('bspo-search-autocomplete');
    if (box) return box;

    box = document.createElement('div');
    box.id = 'bspo-search-autocomplete';
    box.className = 'bspo-search-autocomplete';
    box.setAttribute('role', 'listbox');
    document.body.appendChild(box);
    return box;
  }

  function positionAutocomplete(input) {
    var box = ensureAutocomplete();
    var rect = input.getBoundingClientRect();

    box.style.left = Math.round(rect.left) + 'px';
    box.style.top = Math.round(rect.bottom + 4) + 'px';
    box.style.width = Math.max(320, Math.round(rect.width)) + 'px';
  }

  function closeAutocomplete() {
    var box = document.getElementById('bspo-search-autocomplete');
    if (box) {
      box.classList.remove('is-open');
      box.innerHTML = '';
    }
    document.body.classList.remove('bspo-custom-search-open');
    activeAutocomplete = null;
  }

  function looksLikeEllaAutocomplete(element) {
    if (!element || element.nodeType !== 1) return false;
    if (element.id === 'bspo-search-autocomplete' || element.closest('#bspo-search-autocomplete')) return false;

    var className = String(element.className || '');
    var id = String(element.id || '');
    var signature = (className + ' ' + id).toLowerCase();

    if (
      signature.indexOf('quicksearch') !== -1 ||
      signature.indexOf('livesearch') !== -1 ||
      signature.indexOf('live-search') !== -1 ||
      signature.indexOf('ajax-search') !== -1 ||
      signature.indexOf('search-results') !== -1 ||
      signature.indexOf('search-dropdown') !== -1 ||
      signature.indexOf('search-autocomplete') !== -1
    ) {
      return true;
    }

    var text = String(element.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
    return text.indexOf('SEE ALL RESULTS') !== -1 || text.indexOf('LIVESEARCH') !== -1;
  }

  function hideEllaAutocomplete(root) {
    root = root || document;

    var selectors = [
      '.search-results',
      '#search-results',
      '.ajax-search-results',
      '.quickSearchResultsWrap',
      '.quickSearchResults',
      '.search-results-wrapper',
      '.search-dropdown',
      '.search-autocomplete',
      '.live-search',
      '.live-search-results',
      '[class*="quickSearch"]',
      '[class*="liveSearch"]',
      '[class*="livesearch"]'
    ].join(',');

    var candidates = [];

    if (root.nodeType === 1 && looksLikeEllaAutocomplete(root)) {
      candidates.push(root);
    }

    if (root.querySelectorAll) {
      Array.prototype.forEach.call(root.querySelectorAll(selectors), function (el) {
        candidates.push(el);
      });
    }

    candidates.forEach(function (el) {
      if (!el || el.id === 'bspo-search-autocomplete' || el.closest('#bspo-search-autocomplete')) return;
      el.classList.add('bspo-native-search-hidden');
      el.setAttribute('aria-hidden', 'true');
      el.style.setProperty('display', 'none', 'important');
    });

    // Ella revisions do not always use the same wrapper class. As a fallback,
    // identify the native dropdown by its distinctive footer text.
    if (root.querySelectorAll) {
      Array.prototype.forEach.call(root.querySelectorAll('div,ul'), function (el) {
        if (el.id === 'bspo-search-autocomplete' || el.closest('#bspo-search-autocomplete')) return;
        if (!looksLikeEllaAutocomplete(el)) return;

        var text = String(el.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
        if (text.indexOf('SEE ALL RESULTS') === -1 && text.indexOf('LIVESEARCH') === -1) return;

        var rect = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
        if (rect && rect.width > 250 && rect.height > 40) {
          el.classList.add('bspo-native-search-hidden');
          el.setAttribute('aria-hidden', 'true');
          el.style.setProperty('display', 'none', 'important');
        }
      });
    }
  }

  function watchEllaAutocomplete() {
    hideEllaAutocomplete(document);

    if (!window.MutationObserver || document.documentElement.dataset.bspoEllaWatcher === '1') return;
    document.documentElement.dataset.bspoEllaWatcher = '1';

    var suppressScheduled = false;

    function scheduleSuppression() {
      if (suppressScheduled) return;
      suppressScheduled = true;

      window.requestAnimationFrame(function () {
        suppressScheduled = false;
        if (!document.body.classList.contains('bspo-search-engaged') && !document.body.classList.contains('bspo-custom-search-open')) return;
        hideEllaAutocomplete(document);
      });
    }

    var observer = new MutationObserver(function (mutations) {
      if (!document.body.classList.contains('bspo-search-engaged') && !document.body.classList.contains('bspo-custom-search-open')) return;

      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];

        if (mutation.type === 'attributes' || mutation.type === 'characterData') {
          scheduleSuppression();
          return;
        }

        if (mutation.addedNodes && mutation.addedNodes.length) {
          scheduleSuppression();
          return;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
      characterData: true
    });
  }

  function autocompleteReason(match, query) {
    if (match.kind === 'h') return match.exact ? 'Exact Part Number Match' : 'Part Number Match';
    if (match.kind === 'o') return match.exact ? 'Alternate Part Number Match' : 'Alternate Part Number';
    if (match.kind === 'r') return match.exact ? 'Replacement Match for “' + query + '”' : 'Replacement Part Number';
    if (match.kind === 'd') return 'Model Match';
    if (match.kind === 'w') return 'Compatibility Match';
    return 'Product Match';
  }


  function rowNormalizedText(row, selector) {
    var el = row ? row.querySelector(selector) : null;
    return normalizeExact(el ? el.textContent : '');
  }

  function reorderAutocompleteRows(box) {
    if (!box) return;

    var rows = Array.prototype.slice.call(
      box.querySelectorAll('.bspo-search-autocomplete-item')
    );
    if (rows.length < 2) return;

    var replacementTargets = {};
    rows.forEach(function (row) {
      var replacement = normalizeExact(row.getAttribute('data-bspo-replacement') || '');
      if (replacement) replacementTargets[replacement] = true;
    });

    function isTarget(row) {
      var handle = normalizeExact(row.getAttribute('data-handle') || '');
      var title = rowNormalizedText(row, '.bspo-search-autocomplete-title');

      return Object.keys(replacementTargets).some(function (target) {
        return target && (handle === target || title.indexOf(target) !== -1);
      });
    }

    function statusWeight(row) {
      var status = String(row.getAttribute('data-bspo-status') || '').toLowerCase();
      if (status === 'replacement' || status === 'discontinued') return 0;
      return 1;
    }

    var originalIndex = new Map();
    rows.forEach(function (row, i) { originalIndex.set(row, i); });

    rows.sort(function (a, b) {
      var at = isTarget(a) ? 1 : 0;
      var bt = isTarget(b) ? 1 : 0;
      if (bt !== at) return bt - at;

      var as = statusWeight(a);
      var bs = statusWeight(b);
      if (bs !== as) return bs - as;

      return originalIndex.get(a) - originalIndex.get(b);
    });

    var footer = box.querySelector('.bspo-search-autocomplete-footer');
    rows.forEach(function (row) {
      if (footer) box.insertBefore(row, footer);
      else box.appendChild(row);
    });
  }

  function reorderUnifiedCards(wrap) {
    if (!wrap) return;
    var grid = wrap.querySelector('.bspo-unified-search-grid');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.bspo-unified-card'));
    if (cards.length < 2) return;

    var replacementTargets = {};
    cards.forEach(function (card) {
      var replacement = normalizeExact(card.getAttribute('data-bspo-replacement') || '');
      if (replacement) replacementTargets[replacement] = true;
    });

    function isTarget(card) {
      var handle = normalizeExact(card.getAttribute('data-handle') || '');
      var title = rowNormalizedText(card, '.bspo-unified-card-title');

      return Object.keys(replacementTargets).some(function (target) {
        return target && (handle === target || title.indexOf(target) !== -1);
      });
    }

    function statusWeight(card) {
      var status = String(card.getAttribute('data-bspo-status') || '').toLowerCase();
      if (status === 'replacement' || status === 'discontinued') return 0;
      return 1;
    }

    var originalIndex = new Map();
    cards.forEach(function (card, i) { originalIndex.set(card, i); });

    cards.sort(function (a, b) {
      var at = isTarget(a) ? 1 : 0;
      var bt = isTarget(b) ? 1 : 0;
      if (bt !== at) return bt - at;

      var as = statusWeight(a);
      var bs = statusWeight(b);
      if (bs !== as) return bs - as;

      return originalIndex.get(a) - originalIndex.get(b);
    });

    cards.forEach(function (card) {
      grid.appendChild(card);
    });
  }

  function renderAutocomplete(input, query, merged) {
    var box = ensureAutocomplete();
    var html = '';

    // Own the no-results state too. Previously V10 closed the BSPO dropdown
    // when both native and custom results were empty. That gave Ella's original
    // black live-search UI an opportunity to become visible again.
    if (!merged.length) {
      html =
        '<span class="bspo-search-autocomplete-empty">No matching products found.</span>' +
        '<a class="bspo-search-autocomplete-footer" href="/search?type=product&q=' +
        encodeURIComponent(query) + '&bspo_unified=1">View all search results</a>';

      box.innerHTML = html;
      activeAutocomplete = input;
      positionAutocomplete(input);
      box.classList.add('is-open');
      document.body.classList.add('bspo-custom-search-open');
      document.body.classList.add('bspo-search-engaged');
      hideEllaAutocomplete(document);
      return;
    }

    merged.forEach(function (item) {
      html += '<a class="bspo-search-autocomplete-item" data-handle="' + escapeHtml(item.handle || '') + '" role="option" href="' + escapeHtml(item.url) + '">';

      if (item.thumb) {
        html += '<img class="bspo-search-autocomplete-thumb" src="' + escapeHtml(item.thumb) + '" alt="">';
      }

      html +=
        '<span class="bspo-search-autocomplete-copy">' +
          '<span class="bspo-search-autocomplete-title">' + escapeHtml(item.title) + '</span>' +
          '<span class="bspo-search-autocomplete-reason">' + escapeHtml(item.reason || 'Product Match') + '</span>';

      if (item.price) {
        html += '<span class="bspo-search-autocomplete-price">' + escapeHtml(item.price) + '</span>';
      }

      html += '</span></a>';
    });

    html +=
      '<a class="bspo-search-autocomplete-footer" href="/search?type=product&q=' +
      encodeURIComponent(query) + '&bspo_unified=1">View all search results</a>';

    box.innerHTML = html;
    activeAutocomplete = input;
    positionAutocomplete(input);
    box.classList.add('is-open');
    document.body.classList.add('bspo-custom-search-open');
    hideEllaAutocomplete(document);
    enrichAutocomplete(box, merged, query);
  }

  function enrichAutocomplete(box, items, query) {
    if (!box || !items || !items.length) return;

    var queue = items.slice(0, 10);
    var nextIndex = 0;
    var active = 0;
    var concurrency = 3;

    function findRow(handle) {
      var rows = box.querySelectorAll('.bspo-search-autocomplete-item');
      for (var r = 0; r < rows.length; r++) {
        if (String(rows[r].getAttribute('data-handle') || '').toLowerCase() === String(handle || '').toLowerCase()) return rows[r];
      }
      return null;
    }

    function updateRow(item, data, info) {
      if (!document.body.contains(box)) return;
      var row = findRow(item.handle);
      if (!row) return;

      if (!row.querySelector('.bspo-search-autocomplete-thumb')) {
        var image = (info && info.thumb) || (data && data.featured_image) || '';
        if (image) {
          row.insertAdjacentHTML('afterbegin', '<img class="bspo-search-autocomplete-thumb" src="' +
            escapeHtml(upgradeImageUrl(image)) + '" alt="">');
        }
      }

      var copy = row.querySelector('.bspo-search-autocomplete-copy');
      if (!copy) return;
      var oldPrice = copy.querySelector('.bspo-search-autocomplete-price');
      var oldStatus = copy.querySelector('.bspo-search-autocomplete-status');
      if (oldStatus) oldStatus.remove();

      if (info && info.status) {
        row.setAttribute('data-bspo-status', info.status || '');
        row.setAttribute('data-bspo-replacement', info.replacement || '');
        if (oldPrice) oldPrice.remove();
        copy.insertAdjacentHTML('beforeend', '<span class="bspo-search-autocomplete-status">' +
          escapeHtml(statusText(info)) + '</span>');
      } else {
        row.setAttribute('data-bspo-status', '');
        row.setAttribute('data-bspo-replacement', '');
        if (!oldPrice && data && data.price != null) {
          copy.insertAdjacentHTML('beforeend', '<span class="bspo-search-autocomplete-price">' +
            escapeHtml(moneyFromCents(data.price)) + '</span>');
        }
      }

      reorderAutocompleteRows(box);
    }

    function pump() {
      while (active < concurrency && nextIndex < queue.length) {
        (function (item) {
          active++;
          Promise.all([
            fetchProductJson(item.handle),
            isPartNumberLike(query) ? fetchProductPageInfo(item.handle) : Promise.resolve(null)
          ]).then(function (results) {
            updateRow(item, results[0], results[1]);
          }).then(function () {
            active--;
            pump();
          }, function () {
            active--;
            pump();
          });
        })(queue[nextIndex++]);
      }
    }

    pump();
  }

  function processAutocomplete(input) {
    var query = input.value.trim();
    clearTimeout(autocompleteTimer);

    if (query.length < 2) {
      closeAutocomplete();
      return;
    }

    autocompleteTimer = setTimeout(function () {
      var thisRequest = ++requestNumber;

      Promise.all([
        loadIndex(query),
        fetchNativeLiveSearch(query)
      ]).then(function (results) {
        if (thisRequest !== requestNumber) return;

        var index = results[0];
        var nativeResults = results[1];
        var customMatches = findAutocompleteMatches(index, query);
        var merged = mergeAutocomplete(nativeResults, index, customMatches, query);

        renderAutocomplete(input, query, merged);
      }).catch(function () {
        // Keep Ella suppressed while the BSPO search field is engaged.
        // The native Shopify results page remains the submit fallback.
        closeAutocomplete();
        if (document.activeElement === input) {
          document.body.classList.add('bspo-search-engaged');
          hideEllaAutocomplete(document);
        }
      });
    }, 120);
  }

  function goToCustomMatch(index, query, matches) {
    var exactMatches = matches.filter(function (m) { return m.exact; });

    if (exactMatches.length === 1) {
      var p = product(index, exactMatches[0]);
      window.location.href = '/products/' + encodeURIComponent(p.handle)
        + '?bspo_q=' + encodeURIComponent(query)
        + '&bspo_match=' + encodeURIComponent(p.kind);
      return true;
    }

    if (exactMatches.length > 1 && exactMatches[0].score > exactMatches[1].score) {
      var top = product(index, exactMatches[0]);
      window.location.href = '/products/' + encodeURIComponent(top.handle)
        + '?bspo_q=' + encodeURIComponent(query)
        + '&bspo_match=' + encodeURIComponent(top.kind);
      return true;
    }

    var modelExact = matches.filter(function (m) {
      return m.kind === 'd' && m.exact;
    });

    if (modelExact.length === 1) {
      var mp = product(index, modelExact[0]);
      window.location.href = '/products/' + encodeURIComponent(mp.handle)
        + '?bspo_q=' + encodeURIComponent(query)
        + '&bspo_match=d';
      return true;
    }

    return false;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isSearchForm(form)) return;
    if (form.dataset.bspoNativeSubmit === '1') return;

    var input = getQueryInput(form);
    if (!input) return;

    var query = input.value.trim();
    if (query.length < 2) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    loadIndex(query).then(function (index) {
      if (!isNativeMpn(index, query)) {
        var matches = findMatches(index, query);
        if (goToCustomMatch(index, query, matches)) return;
      }

      // All non-unique searches now go through the unified results view.
      window.location.href = '/search?type=product&q='
        + encodeURIComponent(query) + '&bspo_unified=1';
    }).catch(function () {
      form.dataset.bspoNativeSubmit = '1';
      HTMLFormElement.prototype.submit.call(form);
    });
  }, true);

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!input.matches('form.bspo-search-form input[name="q"], form.bspo-home-search-form input[name="q"], form.search-bar input[name="q"]')) return;

    // Suppress Ella immediately, including on the very first character.
    document.body.classList.add('bspo-search-engaged');
    hideEllaAutocomplete(document);
    processAutocomplete(input);
  }, true);

  document.addEventListener('focusin', function (event) {
    var input = event.target;
    if (!input.matches('form.bspo-search-form input[name="q"], form.bspo-home-search-form input[name="q"], form.search-bar input[name="q"]')) return;

    // Mobile Ella live search can fire before BSPO reaches its 2-character threshold.
    document.body.classList.add('bspo-search-engaged');
    hideEllaAutocomplete(document);
    if (input.value.trim().length >= 2) processAutocomplete(input);
  }, true);

  document.addEventListener('focusout', function (event) {
    var input = event.target;
    if (!input.matches('form.bspo-search-form input[name="q"], form.bspo-home-search-form input[name="q"], form.search-bar input[name="q"]')) return;

    setTimeout(function () {
      var focused = document.activeElement;
      var stillInSearch = focused && focused.closest && focused.closest(
        'form.bspo-search-form, form.bspo-home-search-form, form.search-bar, #bspo-search-autocomplete'
      );

      if (!stillInSearch) document.body.classList.remove('bspo-search-engaged');
    }, 0);
  }, true);

  document.addEventListener('keydown', function (event) {
    var input = event.target;
    if (!input.matches('form.bspo-search-form input[name="q"], form.bspo-home-search-form input[name="q"], form.search-bar input[name="q"]')) return;

    document.body.classList.add('bspo-search-engaged');
    hideEllaAutocomplete(document);

    var box = document.getElementById('bspo-search-autocomplete');
    if (!box || !box.classList.contains('is-open')) return;

    var items = Array.prototype.slice.call(
      box.querySelectorAll('.bspo-search-autocomplete-item')
    );
    if (!items.length) return;

    var active = box.querySelector('.bspo-search-autocomplete-item.is-active');
    var current = active ? items.indexOf(active) : -1;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (active) active.classList.remove('is-active');
      current = (current + 1) % items.length;
      items[current].classList.add('is-active');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (active) active.classList.remove('is-active');
      current = current <= 0 ? items.length - 1 : current - 1;
      items[current].classList.add('is-active');
    } else if (event.key === 'Enter' && active) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.href = active.href;
    } else if (event.key === 'Escape') {
      closeAutocomplete();
    }
  }, true);

  document.addEventListener('click', function (event) {
    var box = document.getElementById('bspo-search-autocomplete');
    if (!box) return;

    var clickedSearch = event.target.closest(
      'form.bspo-search-form, form.bspo-home-search-form, form.search-bar, #bspo-search-autocomplete'
    );
    if (!clickedSearch) {
      closeAutocomplete();
      document.body.classList.remove('bspo-search-engaged');
    }
  });

  window.addEventListener('resize', function () {
    if (activeAutocomplete) positionAutocomplete(activeAutocomplete);
  });

  window.addEventListener('scroll', function () {
    if (activeAutocomplete) positionAutocomplete(activeAutocomplete);
  }, true);

  function upgradeImageUrl(url) {
    var value = String(url || '');
    if (!value) return '';

    // Ella's live-search feed commonly returns a deliberately small Shopify
    // thumbnail. Ask Shopify for a larger derivative when the old filename
    // sizing convention is present. CSS still scales any other image source.
    return value.replace(
      /_(?:pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*|\d*x\d+)(?:_crop_[^.]+)?(\.[a-zA-Z0-9]+)(\?.*)?$/,
      '_360x360$1$2'
    );
  }

  function fetchProductJson(handle) {
    var key = String(handle || '').toLowerCase();
    if (!key) return Promise.resolve(null);
    if (productJsonCache[key]) return productJsonCache[key];

    productJsonCache[key] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    }).then(function (response) {
      if (!response.ok) throw new Error('Product JSON failed.');
      return response.json();
    }).catch(function () {
      return null;
    });

    return productJsonCache[key];
  }

  function fetchProductPageInfo(handle) {
    var key = String(handle || '').toLowerCase();
    if (!key) return Promise.resolve(null);
    if (productPageInfoCache[key]) return productPageInfoCache[key];

    productPageInfoCache[key] = fetch('/products/' + encodeURIComponent(handle), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Product page failed.');
      return response.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var bodyText = doc.body ? String(doc.body.textContent || '') : String(html || '');
      var replacementMatch = bodyText.match(/SEE\s+REPLACEMENT\s+PART\s*:\s*([A-Z0-9][A-Z0-9._\/-]*)/i);
      var discontinued = /DISCONTINUED\s+BY\s+MANUFACTURER/i.test(bodyText);
      var ogImage = doc.querySelector('meta[property="og:image"]');
      var image = ogImage ? String(ogImage.getAttribute('content') || '') : '';

      if (replacementMatch && replacementMatch[1]) {
        return {
          status: 'replacement',
          replacement: String(replacementMatch[1]).trim(),
          thumb: image
        };
      }

      if (discontinued) {
        return { status: 'discontinued', replacement: '', thumb: image };
      }

      return { status: '', replacement: '', thumb: image };
    }).catch(function () {
      return null;
    });

    return productPageInfoCache[key];
  }

  function buildUnifiedItems(index, query, customMatches, nativeResults) {
    var items = [];
    var seen = {};
    var nativeByHandle = {};

    nativeResults.forEach(function (item) {
      var nativeKey = String(item.handle || '').toLowerCase();
      if (nativeKey) nativeByHandle[nativeKey] = item;
    });

    customMatches.forEach(function (match) {
      var p = product(index, match);
      var key = String(p.handle || '').toLowerCase();
      if (!key || seen[key]) return;

      // If Shopify's native search also knows this product, reuse its image
      // and price immediately. V9 discarded that useful data and then made a
      // second /products/HANDLE.js request for the same product.
      var nativeMatch = nativeByHandle[key] || null;

      seen[key] = true;
      items.push({
        source: 'custom',
        handle: p.handle,
        title: p.title,
        url: '/products/' + encodeURIComponent(p.handle)
          + '?bspo_q=' + encodeURIComponent(query)
          + '&bspo_match=' + encodeURIComponent(p.kind),
        thumb: nativeMatch ? upgradeImageUrl(nativeMatch.thumb) : '',
        price: nativeMatch ? nativeMatch.price : '',
        reason: reason(p.kind, query),
        score: p.score + 10000
      });

      if (isPartNumberLike(query) && isDirectQueryMatch(items[items.length - 1], query)) {
        items[items.length - 1].price = '';
      }
    });

    nativeResults.forEach(function (item) {
      var key = String(item.handle || item.url || '').toLowerCase();
      if (!key || seen[key]) return;

      seen[key] = true;
      items.push({
        source: 'native',
        handle: item.handle,
        title: item.title,
        url: item.url,
        thumb: upgradeImageUrl(item.thumb),
        price: item.price,
        reason: 'Product Match',
        score: 100
      });

      if (isPartNumberLike(query) && isDirectQueryMatch(items[items.length - 1], query)) {
        items[items.length - 1].price = '';
      }
    });

    // Do not discard matches. Pagination happens when rendering, never here.
    return items;
  }

  function renderUnifiedSearchPage(index, query, items, enrichCustom) {
    var nativePage = document.querySelector('.search-page');
    if (!nativePage) return null;

    var wrap = document.querySelector('.bspo-unified-search-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'bspo-unified-search-wrap';
      nativePage.insertAdjacentElement('beforebegin', wrap);
    }

    // Show 24 initially to preserve V16 layout and page performance.
    // All results remain available through Show More in the SAME card design.
    var PAGE_SIZE = 24;
    var visibleCount = Math.min(PAGE_SIZE, items.length);
    var catalogUrl = '/search?type=product&q=' + encodeURIComponent(query) + '&bspo_native=1';
    var catalogLink = '<p class="bspo-search-catalog-link"><a href="' +
      escapeHtml(catalogUrl) + '">Browse all Shopify catalog matches</a></p>';

    if (!items.length) {
      wrap.innerHTML =
        '<h2 class="bspo-unified-search-header">Search Results for “' + escapeHtml(query) + '”</h2>' +
        '<div class="bspo-unified-no-results">No enhanced matches found. Try a shorter part number, manufacturer name, model, or broader equipment term.</div>' +
        catalogLink;
      nativePage.style.display = 'none';
      return wrap;
    }

    var html =
      '<h2 class="bspo-unified-search-header">Search Results for “' + escapeHtml(query) + '”</h2>' +
      '<p class="bspo-search-count" role="status">Showing ' + visibleCount + ' of ' + items.length +
        ' indexed search matches</p>' +
      '<div class="bspo-unified-search-grid">';

    function cardHtml(item) {
      return '<a class="bspo-unified-card" data-handle="' + escapeHtml(item.handle || '') + '" href="' + escapeHtml(item.url) + '">' +
          '<span class="bspo-unified-card-image-wrap">' +
            (item.thumb ? '<img class="bspo-unified-card-image" src="' + escapeHtml(item.thumb) + '" alt="" loading="lazy" decoding="async">' : '') +
          '</span>' +
          '<span class="bspo-unified-card-body">' +
            '<span class="bspo-unified-card-title">' + escapeHtml(item.title) + '</span>' +
            '<span class="bspo-unified-card-reason">' + escapeHtml(item.reason || 'Product Match') + '</span>' +
            (item.price ? '<span class="bspo-unified-card-price">' + escapeHtml(item.price) + '</span>' : '') +
          '</span>' +
        '</a>';
    }

    items.slice(0, visibleCount).forEach(function (item) { html += cardHtml(item); });
    html += '</div>';
    if (items.length > visibleCount) {
      html += '<button class="bspo-search-show-more" type="button">Show More Products</button>';
    }
    html += catalogLink;
    wrap.innerHTML = html;
    nativePage.style.display = 'none';

    var moreButton = wrap.querySelector('.bspo-search-show-more');
    if (moreButton) {
      moreButton.addEventListener('click', function () {
        var end = Math.min(visibleCount + PAGE_SIZE, items.length);
        var added = items.slice(visibleCount, end);
        var addedHtml = '';
        added.forEach(function (item) { addedHtml += cardHtml(item); });
        wrap.querySelector('.bspo-unified-search-grid').insertAdjacentHTML('beforeend', addedHtml);
        visibleCount = end;
        var count = wrap.querySelector('.bspo-search-count');
        if (count) count.textContent = 'Showing ' + visibleCount + ' of ' + items.length + ' indexed search matches';
        if (visibleCount >= items.length) moreButton.remove();
        // Enrich only newly revealed cards rather than requesting hundreds of PDPs.
        enrichUnifiedCards(wrap, added, query);
      });
    }

    if (enrichCustom) enrichUnifiedCards(wrap, items.slice(0, visibleCount), query);
    return wrap;
  }

  function enrichUnifiedCards(wrap, items, query) {
    // Enrich EVERY visible result, including subsequent Show More batches.
    // The old .slice(0, 12) caused blank thumbnails on remaining cards.
    // A concurrency-limited queue and per-handle caches limit server requests.
    var queue = items.filter(function (item) {
      return item && item.handle;
    });

    if (!queue.length) return;

    var nextIndex = 0;
    var active = 0;
    var concurrency = 3;

    function pump() {
      while (active < concurrency && nextIndex < queue.length) {
        (function (item) {
          active++;
          Promise.all([
            fetchProductJson(item.handle),
            fetchProductPageInfo(item.handle)
          ]).then(function (results) {
            var data = results[0];
            var info = results[1];
            if (!document.body.contains(wrap)) return;

            var cards = wrap.querySelectorAll('.bspo-unified-card');
            var card = null;
            for (var c = 0; c < cards.length; c++) {
              if (String(cards[c].getAttribute('data-handle') || '').toLowerCase() === String(item.handle).toLowerCase()) {
                card = cards[c];
                break;
              }
            }
            if (!card) return;

            var imageWrap = card.querySelector('.bspo-unified-card-image-wrap');
            if (imageWrap) {
              var currentImage = imageWrap.querySelector('img');
              var verifiedImage = (data && data.featured_image) || (info && info.thumb) || '';
              // Fill missing thumbnails; also replace broken feed thumbnails.
              if (verifiedImage && (!currentImage || (currentImage.complete && currentImage.naturalWidth === 0))) {
                var finalImage = document.createElement('img');
                finalImage.className = 'bspo-unified-card-image';
                finalImage.alt = '';
                finalImage.loading = 'lazy';
                finalImage.decoding = 'async';
                finalImage.src = upgradeImageUrl(verifiedImage);
                imageWrap.innerHTML = '';
                imageWrap.appendChild(finalImage);
              } else if (verifiedImage && currentImage) {
                // Native thumbnail may fail after enrichment has run.
                currentImage.addEventListener('error', function () {
                  if (!imageWrap.contains(currentImage)) return;
                  var retryImage = document.createElement('img');
                  retryImage.className = 'bspo-unified-card-image';
                  retryImage.alt = '';
                  retryImage.loading = 'lazy';
                  retryImage.decoding = 'async';
                  retryImage.src = upgradeImageUrl(verifiedImage);
                  imageWrap.innerHTML = '';
                  imageWrap.appendChild(retryImage);
                }, { once: true });
              }
            }

            var body = card.querySelector('.bspo-unified-card-body');
            if (!body) return;
            var priceEl = body.querySelector('.bspo-unified-card-price');
            var statusEl = body.querySelector('.bspo-unified-card-status');
            if (statusEl) statusEl.remove();

            if (info && info.status) {
              card.setAttribute('data-bspo-status', info.status || '');
              card.setAttribute('data-bspo-replacement', info.replacement || '');
              if (priceEl) priceEl.remove();
              body.insertAdjacentHTML('beforeend', '<span class="bspo-unified-card-status">' +
                escapeHtml(statusText(info)) + '</span>');
            } else {
              card.setAttribute('data-bspo-status', '');
              card.setAttribute('data-bspo-replacement', '');
              if (!priceEl && data && data.price != null) {
                body.insertAdjacentHTML('beforeend', '<span class="bspo-unified-card-price">' +
                  escapeHtml(moneyFromCents(data.price)) + '</span>');
              }
            }

            reorderUnifiedCards(wrap);
          }).then(function () {
            active--;
            pump();
          }, function () {
            active--;
            pump();
          });
        })(queue[nextIndex++]);
      }
    }

    pump();
  }

  function releaseUnifiedLoading() {
    document.documentElement.classList.remove('bspo-unified-loading');
  }

  function enhanceSearchPage() {
    if (window.location.pathname !== '/search') {
      releaseUnifiedLoading();
      return;
    }

    var params = new URLSearchParams(window.location.search);
    var query = (params.get('q') || '').trim();
    // A deliberate fallback to Shopify native search, with its own pagination.
    // The unified script must not cover or hide those results.
    if (params.get('bspo_native') === '1' || query.length < 2) {
      releaseUnifiedLoading();
      return;
    }

    var nativePromise = fetchNativeLiveSearch(query);
    var customIndexPromise = loadIndex(query);
    var finalRendered = false;

    // Fast first paint: Shopify's native results can be useful before the
    // larger BSPO cross-reference index has finished loading. Show them as
    // soon as they arrive, then replace them with the fully merged ranking.
    nativePromise.then(function (nativeResults) {
      if (finalRendered || !nativeResults.length) return;
      var nativeItems = buildUnifiedItems({ p: [] }, query, [], nativeResults);
      renderUnifiedSearchPage(null, query, nativeItems, false);
      releaseUnifiedLoading();
    });

    Promise.all([customIndexPromise, nativePromise]).then(function (results) {
      var index = results[0];
      var nativeResults = results[1];
      var customMatches = dedupe(findTitleMatches(index, query).concat(findMatches(index, query)));
      var items = buildUnifiedItems(index, query, customMatches, nativeResults);

      finalRendered = true;
      renderUnifiedSearchPage(index, query, items, true);
      releaseUnifiedLoading();
    }).catch(function () {
      // If the BSPO index fails but Shopify's native results were already
      // rendered, keep them. Otherwise reveal Ella's original results page.
      releaseUnifiedLoading();
    });
  }

  function showPdpMatchContext() {
    if (window.location.pathname.indexOf('/products/') !== 0) return;

    var params = new URLSearchParams(window.location.search);
    var query = (params.get('bspo_q') || '').trim();
    var kind = params.get('bspo_match') || '';
    if (!query || !kind) return;

    var title = document.querySelector('.product-title');
    if (!title || document.getElementById('bspo-pdp-search-match')) return;

    var note = document.createElement('div');
    note.id = 'bspo-pdp-search-match';
    note.className = 'bspo-pdp-search-match';

    if (kind === 'h') note.textContent = 'Exact part number match for “' + query + '”';
    else note.textContent = reason(kind, query);

    title.insertAdjacentElement('afterend', note);
  }

  function init() {
    addStyles();
    watchEllaAutocomplete();
    enhanceSearchPage();
    showPdpMatchContext();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();